import type { CSSProperties, ReactNode } from 'react';
import { Icono } from '../componentes/Icono';
import type { NombreIcono } from '../componentes/iconos';
import { inicial } from './logica';
import { tintaSobre } from './marca';
import { ENLACES_LEGALES, T } from './textos';
import './cliente.css';

/**
 * Página del cliente (componentes.md §19 y §1i): sin el marco del panel. Fondo negro con la ola roja, una columna de 480,
 * cabecera del negocio (avatar con su inicial) o el logo de Reservaria con «Salir» si está identificado, y el pie (T14):
 * «Ofrecido por Reservaria · Privacidad de [negocio] · Privacidad de Reservaria» en las de un negocio y «Política de
 * privacidad · Condiciones» en las de la Cuenta Reservaria.
 */
/**
 * `logo`: el del negocio (la página del pase, G785); sin él, el avatar con la inicial. `color`: el de marca del avatar
 * (`#RRGGBB`, la página del pase, G978), con la inicial en blanco o tinta según el contraste; sin él, el rojo de Reservaria.
 */
export type Cabecera = { tipo: 'cuenta' } | { tipo: 'negocio'; nombre: string; linea?: string; logo?: string | null; color?: string | null };
/** `reservaria`: una página de un negocio cuyo nombre aún no se conoce (4.9 sin la web pública): «Ofrecido por» y la de Reservaria. */
export type Pie = { tipo: 'cuenta' } | { tipo: 'negocio'; nombre: string; privacidad: string | null } | { tipo: 'reservaria' };

interface Props {
    cabecera: Cabecera;
    pie: Pie;
    onSalir?: () => void;
    volver?: { texto: string; accion: () => void };
    titulo?: { titulo: string; sub: string };
    /** Páginas cortas: centradas en vertical. */
    centrada?: boolean;
    /**
     * Pedir cita (9.9) dentro de la web o del widget del negocio (G618): con su marca (las variables de color de
     * `estiloDeMarca`), fondo hueso y sin el negro ni la ola de Reservaria.
     */
    marca?: CSSProperties;
    /** Idioma de la cabecera y el pie (G928); por defecto, español. */
    idioma?: 'es' | 'en';
    /** Dentro de un widget: sin la cabecera del negocio (la pone la página que lo lleva). */
    sinCabecera?: boolean;
    children: ReactNode;
}

const EN = { salir: 'Sign out', ofrecido: 'Powered by', privacidadDe: (negocio: string) => `${negocio} Privacy`, privacidadReservaria: 'Reservaria Privacy', politica: 'Privacy policy', condiciones: 'Terms' };

/** Enlace legal: sin dirección todavía, texto subrayado sin enlace (como en 1.10). */
export function EnlaceLegal({ url, children }: { url: string | null; children: ReactNode }) {
    return url === null ? <u>{children}</u> : <a href={url} target="_blank" rel="noreferrer">{children}</a>;
}

export function Pagina({ cabecera, pie, onSalir, volver, titulo, centrada = false, marca, sinCabecera = false, idioma = 'es', children }: Props) {
    // G928: la cabecera y el pie en inglés en las páginas que lo piden (10.15).
    const L = idioma === 'en' ? EN : { salir: 'Salir', ofrecido: T.t14.ofrecido, privacidadDe: T.privacidadDe, privacidadReservaria: T.privacidadReservaria, politica: T.t14.politica, condiciones: T.t14.condiciones };
    return (
        <div className={marca === undefined ? 'rv-final rv-pc' : 'rv-pc rv-pc--marca'} style={marca} data-pagina-cliente="" lang={idioma}>
            {marca === undefined && <div className="rv-vivo rv-vivo--ola" aria-hidden="true" />}
            <div className="rv-pc__col">
                {!sinCabecera && <header className="rv-final-osc rv-pc__cab">
                    {cabecera.tipo === 'negocio' ? (
                        <>
                            {cabecera.logo != null && cabecera.logo !== '' ? (
                                <img className="rv-pc__avatar rv-pc__avatar--logo" src={cabecera.logo} alt="" />
                            ) : (
                                <span aria-hidden="true" className="rv-pc__avatar" style={cabecera.color != null && /^#[0-9a-f]{6}$/i.test(cabecera.color) ? { background: cabecera.color, color: tintaSobre(cabecera.color) } : undefined}>
                                    {inicial(cabecera.nombre)}
                                </span>
                            )}
                            <span className="rv-pc__marca">
                                <strong>{cabecera.nombre}</strong>
                                {cabecera.linea !== undefined && <span>{cabecera.linea}</span>}
                            </span>
                        </>
                    ) : (
                        <span className="rv-pc__logo">
                            <span className="rv-ofrecido__logo" role="img" aria-label="Reservaria" />
                        </span>
                    )}
                    {onSalir !== undefined && (
                        <button type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" onClick={onSalir}>
                            <Icono nombre="log-out" />
                            <span>{L.salir}</span>
                        </button>
                    )}
                </header>}
                <main className={`rv-pc__main${centrada ? ' rv-pc__main--centro' : ''}`}>
                    {volver !== undefined && (
                        <button type="button" className="rv-pc__volver" onClick={volver.accion}>
                            <Icono nombre="chevron-left" />
                            <span>{volver.texto}</span>
                        </button>
                    )}
                    {titulo !== undefined && (
                        <div className="rv-final-osc rv-pc__h1">
                            <h1>{titulo.titulo}</h1>
                            <span>{titulo.sub}</span>
                        </div>
                    )}
                    {children}
                </main>
                <footer className="rv-final-osc rv-pc__pie">
                    {pie.tipo === 'negocio' ? (
                        <>
                            <span className="rv-ofrecido">
                                <span>{L.ofrecido}</span>
                                <span className="rv-ofrecido__logo" role="img" aria-label="Reservaria" />
                            </span>
                            <span className="rv-pc__pie-enlaces">
                                <EnlaceLegal url={pie.privacidad}>{L.privacidadDe(pie.nombre)}</EnlaceLegal>
                                <span aria-hidden="true">·</span>
                                <EnlaceLegal url={ENLACES_LEGALES.privacidad}>{L.privacidadReservaria}</EnlaceLegal>
                            </span>
                        </>
                    ) : pie.tipo === 'reservaria' ? (
                        <>
                            <span className="rv-ofrecido">
                                <span>{L.ofrecido}</span>
                                <span className="rv-ofrecido__logo" role="img" aria-label="Reservaria" />
                            </span>
                            <span className="rv-pc__pie-enlaces">
                                <EnlaceLegal url={ENLACES_LEGALES.privacidad}>{L.privacidadReservaria}</EnlaceLegal>
                            </span>
                        </>
                    ) : (
                        <span className="rv-pc__pie-enlaces">
                            <EnlaceLegal url={ENLACES_LEGALES.privacidad}>{L.politica}</EnlaceLegal>
                            <span aria-hidden="true">·</span>
                            <EnlaceLegal url={ENLACES_LEGALES.condiciones}>{L.condiciones}</EnlaceLegal>
                        </span>
                    )}
                </footer>
            </div>
        </div>
    );
}

/** Baldosa de 56 de las páginas de un toque y de los pasos con icono. */
export function Baldosa({ icono, fondo, color }: { icono: NombreIcono; fondo: string; color: string }) {
    return (
        <span aria-hidden="true" className="rv-pc-baldosa" style={{ background: fondo, color }}>
            <Icono nombre={icono} />
        </span>
    );
}

/** Botón de las páginas del cliente: principal rojo de 56 a todo el ancho, secundario blanco, pequeño de 44. */
export function BotonPc({
    texto,
    icono,
    onClick,
    tipo = 'pri',
    ocupado = false,
    ocupadoTexto,
    peq = false,
    rojo = false,
    desactivado = false,
}: {
    texto: string;
    icono: NombreIcono;
    onClick: () => void;
    tipo?: 'pri' | 'sec';
    ocupado?: boolean;
    ocupadoTexto?: string;
    peq?: boolean;
    rojo?: boolean;
    desactivado?: boolean;
}) {
    const clases = ['rv-pc-b', `rv-pc-b--${tipo}`, peq ? 'rv-pc-b--peq' : tipo === 'sec' ? 'rv-pc-b--ancho' : '', rojo ? 'rv-pc-b--rojo' : ''].filter((c) => c !== '').join(' ');
    return (
        <button
            type="button"
            className={clases}
            aria-busy={ocupado}
            disabled={desactivado}
            onClick={() => {
                if (!ocupado) {
                    onClick();
                }
            }}
        >
            <Icono nombre={ocupado ? 'loader-circle' : icono} className={ocupado ? 'rv-gira' : undefined} />
            <span>{ocupado ? (ocupadoTexto ?? texto) : texto}</span>
        </button>
    );
}

/** Aviso dentro de una tarjeta: error (rojo), espera (amarillo) o información (gris). */
export function AvisoPc({ texto, tipo = 'error' }: { texto: string; tipo?: 'error' | 'espera' | 'info' }) {
    const icono: NombreIcono = tipo === 'espera' ? 'clock' : tipo === 'info' ? 'circle-check' : 'circle-alert';
    return (
        <div role="alert" className={`rv-pc-aviso${tipo === 'error' ? '' : ` rv-pc-aviso--${tipo}`}`}>
            <Icono nombre={icono} />
            <span>{texto}</span>
        </div>
    );
}

/**
 * Aviso con título y texto aparte (10.15, banda de «Mis entradas»): `hecho` en tinta con el visto y `cuidado` en amarillo con
 * el triángulo; el título en negrita y el texto debajo, más suave.
 */
export function AvisoTituladoPc({ titulo, texto, tipo }: { titulo: string; texto: string; tipo: 'hecho' | 'cuidado' }) {
    return (
        <div role="status" className={`rv-pc-banda rv-pc-banda--${tipo}`}>
            <Icono nombre={tipo === 'hecho' ? 'circle-check' : 'triangle-alert'} />
            <span>
                <b>{titulo}</b>
                {texto !== '' && <span>{texto}</span>}
            </span>
        </div>
    );
}

/** Pie legal de reCAPTCHA (T6), con los dos enlaces de Google (G76). */
export function LegalCaptcha() {
    return (
        <p className="rv-pc-legal">
            {T.t6.antes}{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                {T.t6.privacidad}
            </a>{' '}
            {T.t6.y}{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
                {T.t6.condiciones}
            </a>{' '}
            {T.t6.despues}
        </p>
    );
}
